import sanityClient, { ClientConfig } from "@sanity/client";
import { useStaticQueryWithPreview } from "@tedgustaf/gatsby-plugin-sanity-preview";
import { graphql, useStaticQuery } from "gatsby";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import Moment from "react-moment";
import { SanityNode } from "../config/build/models/node";
import siteConfig from "../config/siteConfig";
import { HydrationContext } from "../context/HydrationContext";
import { PageContext } from "../context/PageContext";
import useTranslations from "../hooks/useTranslations";
import { latestNewsPreviewQuery } from "../preview/staticQueries/latestNews";
import { isNullOrWhiteSpace } from "../shared/string";
import { compareLanguages } from "../utilities/routes/utilities";
import CardList from "./CardList";
import { PortableTextColorThemeProps } from "./PortableText/PortableText";
import SanityLink from "./SanityLink";

enum NewsItemType {
  PressRelease = "pressRelease",
  BlogPost = "blogArticle"
}

export type LatestNewsQueryResult = {
  newsItems: NewsItem[];
  allPressReleaseLists: { nodes: SanityNode[] };
  allBlogLists: { nodes: SanityNode[] };
};

export interface NewsItem extends SanityNode {
  teaserDescription: string;
  publishDate: string;
}

export interface LatestNewsProps {
  activeNewsItemType?: NewsItemType | "all";
  displayArchiveLink?: boolean;
  newsItemsOverride?: NewsItem[];
  portableTextColorThemeProps?: PortableTextColorThemeProps;
  subtitle?: string;
  title?: string;
}

const LatestNews = (props: LatestNewsProps) => {
  const {
    activeNewsItemType,
    displayArchiveLink,
    newsItemsOverride,
    portableTextColorThemeProps,
    subtitle,
    title
  } = props;

  const pageContext = useContext(PageContext);

  const translations = useTranslations();

  const data = useStaticQueryWithPreview({
    staticQueryData: useStaticQuery<LatestNewsQueryResult>(graphql`
      fragment NewsItemFragment on NewsItem {
        _id
        _type
        publishDate
        teaserDescription
        title
        slug {
          current
        }
        i18nLang
      }

      query GetNewsItemsQuery {
        newsItems: allNewsItems {
          ...NewsItemFragment
        }

        allBlogLists: allSanityBlogList(filter: { slug: { current: { ne: null } } }) {
          nodes {
            slug {
              current
            }
            i18nLang
          }
        }

        allPressReleaseLists: allSanityPressReleaseList(
          filter: { slug: { current: { ne: null } } }
        ) {
          nodes {
            slug {
              current
            }
            i18nLang
          }
        }
      }
    `),
    query: latestNewsPreviewQuery
  });

  const pressReleaseList = data.allPressReleaseLists?.nodes?.find(pressReleaseList =>
    compareLanguages(pressReleaseList.i18nLang, pageContext.i18nLang)
  );

  const pressReleaseListLinkInfo = {
    title: translations.seeAll ?? undefined,
    reference: pressReleaseList
  };

  const blogList = data.allBlogLists?.nodes?.find(blogList =>
    compareLanguages(blogList.i18nLang, pageContext.i18nLang)
  );

  const blogListLinkInfo = {
    title: translations.seeAll ?? undefined,
    reference: blogList
  };

  const initializeNewsItems = () => {
    // Make a copy to not mutate passed props
    let items: NewsItem[] = newsItemsOverride ? [...newsItemsOverride] : [];

    // Fill out the items array up to 3 items
    if (items.length < 3) {
      let itemsFromQuery: NewsItem[] =
        data.newsItems?.filter(item =>
          compareLanguages(item.i18nLang, pageContext.i18nLang)
        ) ?? [];

      if (activeNewsItemType !== "all") {
        itemsFromQuery = itemsFromQuery.filter(item => item._type === activeNewsItemType);
      }

      if (items.length > 0) {
        // Filter out duplicate items
        itemsFromQuery = itemsFromQuery.filter(
          queryItem => items.some(item => item._id === queryItem._id) === false
        );
      }

      itemsFromQuery = itemsFromQuery.slice(0, 3 - items.length);

      items = items.concat(itemsFromQuery);
    }

    items.sort((left, right) => {
      const leftDate = new Date(left.publishDate);
      const rightDate = new Date(right.publishDate);
      return leftDate > rightDate ? -1 : 1;
    });

    return items;
  };

  const [latestNewsItems, setLatestNewsItems] = useState<NewsItem[]>(
    initializeNewsItems()
  );

  // Refresh with latest news items upon page load
  useEffect(() => {
    if (newsItemsOverride === undefined || newsItemsOverride.length === 0) {
      const client = sanityClient({
        projectId: siteConfig.sanityProjectId,
        dataset: siteConfig.sanityDataset,
        token: siteConfig.sanityReadToken,
        useCdn: false,
        apiVersion: siteConfig.sanityApiVersion
      } as ClientConfig);

      let query: string;

      const params: Record<string, unknown> = {
        currentTime: moment().format(moment.defaultFormatUtc),
        lang: pageContext.i18nLang
      };

      if (activeNewsItemType === "all") {
        query = `
          *[
            i18nLang == $lang &&
            publishDate < $currentTime &&
            !(_id in path("drafts.**")) &&
            (
              _type == "pressRelease" || 
              _type == "blogArticle"
            )
          ] | order(publishDate desc)[0..2]
        `.trim();
      } else {
        query = `
          *[
            i18nLang == $lang &&
            publishDate < $currentTime &&
            !(_id in path("drafts.**")) &&
            _type == $type
          ] | order(publishDate desc)[0..2]
        `.trim();

        params.type = activeNewsItemType;
      }

      client.fetch<NewsItem[]>(query, params).then(result => {
        if (
          result.some(
            (resultItem, index) =>
              resultItem.publishDate !== latestNewsItems[index].publishDate
          )
        ) {
          setLatestNewsItems(result);
        }
      });
    }
  }, [activeNewsItemType, latestNewsItems, newsItemsOverride, pageContext.i18nLang]);

  const hydrated = useContext(HydrationContext);

  return (
    <CardList
      title={title}
      subtitle={subtitle}
      gridBreakpointStyle="row"
      portableTextColorThemeProps={portableTextColorThemeProps}
      displayLink={displayArchiveLink}
      parentLink={
        activeNewsItemType === NewsItemType.BlogPost
          ? blogListLinkInfo
          : pressReleaseListLinkInfo
      }
    >
      {latestNewsItems?.map((item, index) => (
        <SanityLink
          key={index}
          isExternalLink={false}
          reference={item}
          className="flex flex-col p-4 no-underline text-white h-full w-full"
        >
          <div className="flex justify-between mb-2">
            <p className="text-xs text-subheader-primary font-bold uppercase mb-0">
              {!isNullOrWhiteSpace(item._type) &&
                (translations[item._type] ?? item._type)}
            </p>
            <p className="text-xs text-subheader-primary uppercase mb-0 lg:font-bold">
              {hydrated ? <Moment format="ll">{item.publishDate}</Moment> : <>&nbsp;</>}
            </p>
          </div>
          <div className="">
            <h4 className="text-xl font-bold text-brand-primary-20 mb-0">{item.title}</h4>
            <p className="text-base mt-1 mb-0 leading-tight">{item.teaserDescription}</p>
          </div>
        </SanityLink>
      ))}
    </CardList>
  );
};

export default LatestNews;
